<template>
  <div class="attachment-placeholder media-caption" :class="cssClass">
    <template v-if="isProcessing">
      <inline-loader v-if="isLoading" />
    </template>
    <template v-else-if="isInfo">
      <span class="icon">
        <!-- If title is shown, icon is to be shown together as well -->
        <svg-icon v-if="showTitle && hasIconType" :name="iconType" :kind="iconKind" />
        <!-- For google attachment need to show icon as img -->
        <img v-if="hasIconSrc" :src="iconSrc" />
        <svg-icon v-if="showError" class="error" name="Error16" kind="indicative" />
      </span>

      <ellipsis-text
        v-if="showTitle && !hasSecondaryTitle"
        class="title non-highlightable"
        :text="mediaTitle"
      />
      <div v-if="hasSecondaryTitle" class="title">
        <ellipsis-text v-if="showTitle" class="non-highlightable" :text="mediaTitle" />
        <slot v-if="hasSecondaryTitle" name="secondary-title" />
      </div>

      <div v-if="mediaSize" class="title">
        <ellipsis-text v-if="showTitle" class="non-highlightable" :text="mediaSize" />
      </div>

      <slot v-if="hasEmbedUrlSlot" name="embed-url" />

      <div v-if="hasZoomToolsSlot || hasActionToolsSlot || isLoading" class="tools">
        <slot v-if="hasZoomToolsSlot" name="zoom-tools" />
        <slot v-if="hasActionToolsSlot" name="action-tools" />
        <inline-loader v-if="isLoading" />
      </div>
      <message-component v-if="showMessage" :class="messageClasses">
        <template v-if="showVirusScan">
          {{ t("file_upload_processing_virus_scan") }}
        </template>
        <template v-else-if="showError">
          <p class="title non-highlightable">{{ error.title }}</p>
          <p v-if="error.message" class="content non-highlightable">{{ error.message }}</p>
        </template>
      </message-component>
    </template>
    <template v-else-if="isError">
      <message-component v-if="showError && !isErrorMessageAList" :class="messageClasses">
        <p class="title non-highlightable">{{ error.title }}</p>
        <p v-if="error.message" class="content non-highlightable">{{ error.message }}</p>
      </message-component>
      <message-component v-if="showError && isErrorMessageAList" :class="messageClasses">
        <p class="title non-highlightable">{{ error.title }}</p>
        <p
          v-for="message in error.message"
          :key="message"
          v-safe-html="message"
          :data-text="message"
          class="content non-highlightable"
        ></p>
      </message-component>
    </template>
    <template v-else-if="isMessage">
      <!-- show message without info/error color -->
      <!-- For google attachment need to show icon as when it exist -->
      <span class="icon">
        <svg-icon v-if="hasIconType" :name="iconType" :kind="iconKind" />
      </span>
      <message-component>
        <p class="title non-highlightable">{{ error.title }}</p>
      </message-component>
      <div class="tools">
        <slot v-if="hasActionToolsSlot" name="action-tools" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUpdate, useSlots } from "vue";
import { useI18n } from "vue-i18n";

// Types
interface ErrorObject {
  title?: string;
  message?: string | string[];
}

// Props
interface Props {
  kind?: string;
  mediaTitle?: string;
  mediaSize?: string;
  iconKind?: string;
  isLoading?: boolean;
  isVirusScan?: boolean;
  error?: ErrorObject;
  iconType?: string;
  iconSrc?: string;
  showTitle?: boolean;
}

const {
  kind = "info",
  mediaTitle = "",
  mediaSize,
  iconKind = "indicative",
  isLoading = false,
  isVirusScan = false,
  error = {} as ErrorObject,
  iconType = "",
  iconSrc = "",
  showTitle = true,
} = defineProps<Props>();

// Composables
const { t } = useI18n();
const slots = useSlots();

// Reactive data
const hasActionToolsSlot = ref(false);
const hasZoomToolsSlot = ref(false);
const hasEmbedUrlSlot = ref(false);
const hasSecondaryTitle = ref(false);

// Computed properties
const isError = computed(() => kind === "error");
const isInfo = computed(() => kind === "info");
const isMessage = computed(() => kind === "msg");
const isProcessing = computed(() => kind === "processing");

const isErrorMessageAList = computed(() => Array.isArray(error.message));

const showError = computed(() => {
  return (
    error &&
    ((error.title !== undefined && error.title !== "") ||
      (error.message !== undefined && error.message !== ""))
  );
});

const showVirusScan = computed(() => {
  return isLoading && isVirusScan;
});

const showMessage = computed(() => {
  return showVirusScan.value || showError.value;
});

const messageClasses = computed(() => {
  let messageClasses = "";

  if (isError.value) {
    messageClasses = "error";
  } else if (isInfo.value) {
    messageClasses = "info";
  }
  return messageClasses;
});

const hasIconType = computed(() => {
  return iconType && iconType.length > 0;
});

const hasIconSrc = computed(() => {
  return iconSrc && iconSrc.length > 0;
});

const hasNoTools = computed(() => {
  return !hasZoomToolsSlot.value && !hasEmbedUrlSlot.value && !hasActionToolsSlot.value;
});

const cssClass = computed(() => ({
  "has-no-tools": hasNoTools.value,
}));

// Methods
const checkSlots = () => {
  nextTick(() => {
    hasActionToolsSlot.value = !!slots["action-tools"];
    hasZoomToolsSlot.value = !!slots["zoom-tools"];
    hasEmbedUrlSlot.value = !!slots["embed-url"];
    hasSecondaryTitle.value = !!slots["secondary-title"];
  });
};

// Lifecycle hooks
onMounted(() => {
  checkSlots();
});

onBeforeUpdate(() => {
  checkSlots();
});
</script>

<style lang="scss">
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.attachment-placeholder {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  padding: $spacing-03;
  border-top: 1px solid $ui-02;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  &.has-no-tools {
    .title {
      // Allocate space for the icon
      max-width: calc(100% - 3rem);
    }
  }

  > .icon {
    display: flex;
    align-items: center;
    margin-right: $spacing-03;

    &:empty {
      display: none;
    }

    svg {
      &.error {
        margin-right: $spacing-03;
        fill: $support-01;
      }
    }
  }

  .embed-url {
    @include carbon--type-style("label-02", true);
    word-break: break-all;
  }

  .rich-text-editor & {
    background-color: $ui-background;
  }

  > .title {
    // When title is too long, if there are > 1 buttons will shift downwards
    flex: 0 1 auto;
    width: auto;
    max-width: calc(100% - 6rem);
    margin-right: $spacing-03;
    // to fix text cut off issue due to inherit lineheight from tinymce
    line-height: normal;

    &:empty {
      display: none;
    }

    table & {
      max-width: calc(100% - $spacing-09);
    }

    > svg {
      margin-right: $spacing-03;

      &.error {
        fill: $support-01;
      }
    }
  }

  .tools {
    display: flex;
    flex: 1;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: $spacing-03;

    .fit-to-width {
      margin-right: auto;
    }

    > div {
      display: flex;
      align-items: center;

      &.star-resource-button-wrapper {
        margin-left: auto;

        .star-resource-button {
          padding: $spacing-02;
        }
      }
    }

    .action-tools {
      gap: $spacing-03;
    }

    .action {
      .bx--tooltip__trigger {
        z-index: z-map("default");

        .free-response & {
          // higher value than content switcher to prevent overlap
          z-index: calc(z-map("default") + 3);
        }
      }
    }

    .note {
      &._active {
        &:hover {
          background-color: $hover-ui;
        }

        background-color: $active-ui;
        border-radius: 10px;
      }

      .bx--tooltip__trigger {
        svg {
          outline: none;
        }
      }
    }

    .zoom-reset {
      margin-right: auto;
    }

    > button,
    .action-tools > button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      padding: $spacing-02;
      color: transparent;
      cursor: pointer;
      border-radius: 100%;
      min-width: 32px;
      min-height: 32px;

      &[disabled] {
        cursor: not-allowed;
        opacity: 1;

        .bx--tooltip__trigger {
          cursor: not-allowed;

          .sls-icon {
            opacity: 0.5;
          }
        }
      }

      &:focus {
        outline: none;
      }

      &:hover {
        &:not([disabled]) {
          background-color: $hover-ui;
        }
      }
    }
  }

  .message-component {
    flex: 0 0 100%;

    > span {
      width: 100%;
    }

    .content {
      @include carbon--type-style("label-01", true);
      color: $text-01;
      margin-top: $spacing-02;
    }
  }
}
</style>
